import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Pattern, renderPatternToSceneOptimized } from '@av-platform/rendering-core';

interface CombinedPatternPreviewProps {
  foreground?: Pattern | null;
  foregroundColor?: string;
  foregroundScale?: number;
  background?: Pattern | null;
  backgroundColor?: string;
  backgroundScale?: number;
  patternScale?: number;
}

const isSolid = (p: Pattern | null | undefined): boolean => !!p && p.lines.length === 0;

const CombinedPatternPreview = ({
  foreground,
  foregroundColor,
  foregroundScale,
  background,
  backgroundColor,
  backgroundScale,
  patternScale = 1,
}: CombinedPatternPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const bgIsSolid = isSolid(background);
  const fgIsSolid = isSolid(foreground);
  const bothSolidOrEmpty = (!background || bgIsSolid) && (!foreground || fgIsSolid);

  useEffect(() => {
    // Skip THREE.js when everything is solid — CSS handles it
    if (bothSolidOrEmpty) return;
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth || 400;
    const containerHeight = container.clientHeight || 300;

    const camera = new THREE.OrthographicCamera(
      -containerWidth / 2,
      containerWidth / 2,
      containerHeight / 2,
      -containerHeight / 2,
      0.1,
      2000
    );
    camera.position.set(0, 0, 100);
    camera.lookAt(0, 0, 0);

    const clipBounds = Math.max(containerWidth, containerHeight);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    if (background) {
      if (bgIsSolid) {
        const geo = new THREE.PlaneGeometry(clipBounds * 2, clipBounds * 2);
        const mat = new THREE.MeshBasicMaterial({ color: backgroundColor || '#000000' });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.z = -1;
        mesh.userData = { isPattern: true, layerType: 'background' };
        scene.add(mesh);
      } else {
        renderPatternToSceneOptimized(scene, background, {
          patternScale: backgroundScale ?? patternScale,
          color: backgroundColor || '#000000',
          layerType: 'background',
          clipBounds,
        });
      }
    }

    if (foreground) {
      if (fgIsSolid) {
        const geo = new THREE.PlaneGeometry(clipBounds * 2, clipBounds * 2);
        const mat = new THREE.MeshBasicMaterial({ color: foregroundColor || '#000000' });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.z = 0;
        mesh.userData = { isPattern: true, layerType: 'foreground' };
        scene.add(mesh);
      } else {
        renderPatternToSceneOptimized(scene, foreground, {
          patternScale: foregroundScale ?? patternScale,
          color: foregroundColor || '#000000',
          layerType: 'foreground',
          clipBounds,
        });
      }
    }

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      if (newWidth <= 0 || newHeight <= 0) return;
      camera.left = -newWidth / 2;
      camera.right = newWidth / 2;
      camera.top = newHeight / 2;
      camera.bottom = -newHeight / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
      scene.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: any) => m.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      renderer.dispose();
    };
  }, [
    foreground,
    foregroundColor,
    foregroundScale,
    background,
    backgroundColor,
    backgroundScale,
    patternScale,
    bgIsSolid,
    fgIsSolid,
    bothSolidOrEmpty,
  ]);

  // Pure CSS path — no WebGL needed
  if (bothSolidOrEmpty) {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
        {background && bgIsSolid && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: backgroundColor || '#000000',
            }}
          />
        )}
        {foreground && fgIsSolid && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: foregroundColor || '#000000',
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
    />
  );
};

export default CombinedPatternPreview;
